import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

const MeasurementFormPDF = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div data-testid="measurement-form-pdf" className="min-h-screen pt-20 bg-background print:pt-0 print:bg-white">
      {/* Header - Hidden on print */}
      <section className="py-6 border-b border-white/5 print:hidden">
        <div className="max-w-[1200px] mx-auto px-6 flex justify-between items-center">
          <div>
            <h1 className="font-serif text-2xl">Measurement Form</h1>
            <p className="text-sm text-muted-foreground">Print this form and fill in your measurements</p>
          </div>
          <Button
            onClick={handlePrint}
            className="bg-primary text-primary-foreground px-8 py-4 text-xs uppercase tracking-[0.2em] rounded-none"
          >
            <Printer size={16} className="mr-2" /> Print Form
          </Button>
        </div>
      </section>

      {/* Printable Form */}
      <div className="max-w-[900px] mx-auto p-4 print:p-0 print:max-w-full">
        <style>{`
          @media print {
            @page { margin: 0.25in; size: letter; }
            body, html { background: white !important; }
            nav, footer, header, .print\\:hidden { display: none !important; }
            .print-form { 
              background: white !important; 
              color: black !important;
              font-size: 8px !important;
              line-height: 1.2 !important;
            }
            .print-form * { color: black !important; border-color: #333 !important; }
            .print-form table { font-size: 8px !important; }
            .print-form th, .print-form td { padding: 2px 4px !important; }
          }
        `}</style>

        <div className="print-form bg-white text-black p-4 print:p-1 text-[10px]">
          {/* Form Header */}
          <div className="text-center border-b-2 border-black pb-2 mb-3">
            <h1 className="text-xl font-serif tracking-wider mb-0">LAGUERRE</h1>
            <p className="text-[8px] tracking-widest uppercase">Custom Suit Order Form</p>
          </div>

          {/* Client Info */}
          <div className="grid grid-cols-5 gap-2 mb-2 text-[9px]">
            <div className="flex items-center gap-1">
              <span className="font-bold">Date:</span>
              <div className="flex-1 border-b border-black h-4"></div>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-bold">Order#:</span>
              <div className="flex-1 border-b border-black h-4"></div>
            </div>
            <div className="flex items-center gap-1 col-span-2">
              <span className="font-bold">Client:</span>
              <div className="flex-1 border-b border-black h-4"></div>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-bold">Ht/Wt:</span>
              <div className="flex-1 border-b border-black h-4"></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-2 text-[9px]">
            <div className="flex items-center gap-1">
              <span className="font-bold">Fabric:</span>
              <div className="flex-1 border-b border-black h-4"></div>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-bold">Lining:</span>
              <div className="flex-1 border-b border-black h-4"></div>
            </div>
          </div>

          {/* Fit & Body Type */}
          <div className="flex gap-4 mb-3 p-1 bg-gray-100 border border-black text-[8px]">
            <div className="flex items-center gap-1">
              <span className="font-bold">FIT:</span>
              {["Slim", "Reg", "Loose"].map(o => (
                <label key={o} className="flex items-center gap-0.5 ml-1"><div className="w-2 h-2 border border-black"></div>{o}</label>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-bold">STOMACH:</span>
              {["Thin", "Norm", "Med", "Lg"].map(o => (
                <label key={o} className="flex items-center gap-0.5 ml-1"><div className="w-2 h-2 border border-black"></div>{o}</label>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-bold">SHOULDER:</span>
              {["Norm", "Slope", "High"].map(o => (
                <label key={o} className="flex items-center gap-0.5 ml-1"><div className="w-2 h-2 border border-black"></div>{o}</label>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-bold">SEAT:</span>
              {["Thin", "Norm", "Lg"].map(o => (
                <label key={o} className="flex items-center gap-0.5 ml-1"><div className="w-2 h-2 border border-black"></div>{o}</label>
              ))}
            </div>
          </div>

          {/* JACKET, PANTS, VEST */}
          <div className="grid grid-cols-3 gap-3 mb-3">
            {/* JACKET */}
            <div>
              <h2 className="text-[10px] font-bold bg-black text-white px-2 py-1 mb-1">JACKET</h2>
              <table className="w-full border-collapse text-[8px]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-black p-0.5 text-left">Measure</th>
                    <th className="border border-black p-0.5 w-12">Body</th>
                    <th className="border border-black p-0.5 w-12">Fin</th>
                  </tr>
                </thead>
                <tbody>
                  {["Neck", "Chest", "Stomach", "Hip", "Bicep L", "Bicep R", "Wrist L", "Wrist R", "Shoulder", "Sleeve L", "Sleeve R", "Front Len", "Back Len", "1st Btn"].map(item => (
                    <tr key={item}>
                      <td className="border border-black p-0.5">{item}</td>
                      <td className="border border-black p-0.5"></td>
                      <td className="border border-black p-0.5"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PANTS */}
            <div>
              <h2 className="text-[10px] font-bold bg-black text-white px-2 py-1 mb-1">PANTS</h2>
              <table className="w-full border-collapse text-[8px]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-black p-0.5 text-left">Measure</th>
                    <th className="border border-black p-0.5 w-12">Body</th>
                    <th className="border border-black p-0.5 w-12">Fin</th>
                  </tr>
                </thead>
                <tbody>
                  {["Waist", "Hip", "Thigh", "Knee", "Crotch", "L Outseam", "R Outseam", "Hem"].map(item => (
                    <tr key={item}>
                      <td className="border border-black p-0.5">{item}</td>
                      <td className="border border-black p-0.5"></td>
                      <td className="border border-black p-0.5"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* VEST */}
            <div>
              <h2 className="text-[10px] font-bold bg-black text-white px-2 py-1 mb-1">VEST</h2>
              <table className="w-full border-collapse text-[8px]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-black p-0.5 text-left">Measure</th>
                    <th className="border border-black p-0.5 w-12">Body</th>
                    <th className="border border-black p-0.5 w-12">Fin</th>
                  </tr>
                </thead>
                <tbody>
                  {["Chest", "Stomach", "Back Len", "Front Len", "1st Btn", "Bottom"].map(item => (
                    <tr key={item}>
                      <td className="border border-black p-0.5">{item}</td>
                      <td className="border border-black p-0.5"></td>
                      <td className="border border-black p-0.5"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* JACKET STYLE */}
          <div className="mb-2 p-1.5 border border-black text-[8px]">
            <h3 className="font-bold text-[9px] mb-1 border-b border-black pb-0.5">JACKET STYLE</h3>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Style#:</span>
                <div className="border-b border-black w-12 h-3"></div>
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Const:</span>
                {["Fused", "½Canv"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Front:</span>
                {["1B", "2B", "DB"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Lapel:</span>
                {["Notch", "Peak", "Shawl"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Lapel":</span>
                <div className="border-b border-black w-6 h-3"></div>
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">LapHole:</span>
                {["Work", "No"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Pkt:</span>
                {["Flap", "Jet", "Patch"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">SlvBtn:</span>
                {["2", "3", "4"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">SlvHole:</span>
                {["Work", "No"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Vent:</span>
                {["1", "2", "0"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">TktPkt:</span>
                {["Y", "N"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-1 pt-1 border-t border-gray-300">
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Mono:</span>
                <div className="border-b border-black w-14 h-3"></div>
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Style:</span>
                <div className="border-b border-black w-10 h-3"></div>
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Color:</span>
                <div className="border-b border-black w-10 h-3"></div>
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Pos:</span>
                <div className="border-b border-black w-10 h-3"></div>
              </div>
            </div>
          </div>

          {/* PANTS STYLE */}
          <div className="mb-2 p-1.5 border border-black text-[8px]">
            <h3 className="font-bold text-[9px] mb-1 border-b border-black pb-0.5">PANTS STYLE</h3>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Pleat:</span>
                {["0", "1", "2"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Tab:</span>
                {["Reg", "Rnd", "Pt", "Sq"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">Waist:</span>
                {["Clear", "Loop", "Ext"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">FrPkt:</span>
                {["Slant", "Str"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">BkPkt:</span>
                {["1L", "1R", "2"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
              <div className="flex items-center gap-0.5">
                <span className="font-bold">BkStyle:</span>
                {["Btn", "No", "Flap"].map(o => (
                  <label key={o} className="flex items-center gap-0.5 ml-0.5"><div className="w-2 h-2 border border-black"></div>{o}</label>
                ))}
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div className="mb-2">
            <h3 className="font-bold text-[9px] mb-0.5">REMARKS:</h3>
            <div className="border border-black h-12"></div>
          </div>

          {/* Footer */}
          <div className="pt-1 border-t border-black text-center text-[7px]">
            <p className="font-bold">laguerre.co | @laguerre.co | hello@laguerre.co | frerelaguerre@gmail.com | 55 W Railroad Ave (Bldg 24 Suite M2), Garnerville, NY 10923</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeasurementFormPDF;
